import { DialogComponent } from "@theme/dialog";
import { SellingPlanUpdateEvent } from "@theme/events";

/**
 * @typedef {object} GRFrequencyModalRefs
 * @property {HTMLDialogElement} dialog - The dialog element
 * @property {HTMLButtonElement} closeButton - The close button
 * @property {HTMLButtonElement[]} frequencyButtons - Array of frequency option buttons
 */

/**
 * Provides an alternative way to select subscription frequencies by household size.
 * Syncs selection with the main frequency dropdown in gr-product-options.
 *
 * @extends DialogComponent
 */
class GRFrequencyModal extends DialogComponent {
  requiredRefs = ["dialog"];

  #abortController = new AbortController();

  /** @type {Element | null} */
  #eventTarget = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    // Scope events to closest container - prioritize drawer/dialog contexts over page sections
    this.#eventTarget =
      this.closest("added-to-cart-drawer") ||
      this.closest("gr-product-options") ||
      this.closest("dialog") ||
      this.closest(".shopify-section") ||
      this.closest("[data-block-id]") ||
      this.parentElement;

    // Listen for frequency changes from the dropdown to sync selection
    this.#eventTarget?.addEventListener(
      SellingPlanUpdateEvent.eventName,
      this.#onSellingPlanUpdate,
      { signal },
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Handles selling plan update events to sync the modal's selection state
   * @param {CustomEvent} event
   */
  #onSellingPlanUpdate = (event) => {
    const { frequency, sourceId } = event.detail;

    if (sourceId === this.id) return;

    this.#updateSelectedButton(frequency);
  };

  /**
   * Updates which button is marked as selected
   * @param {string | null} frequency
   */
  #updateSelectedButton(frequency) {
    const buttons = this.refs.frequencyButtons;
    if (!buttons || !Array.isArray(buttons)) return;

    for (const button of buttons) {
      if (!(button instanceof HTMLButtonElement)) continue;
      const buttonFrequency = button.dataset.frequency;
      const isSelected = buttonFrequency === frequency;
      button.setAttribute("data-selected", String(isSelected));

      // Find the text div inside the button (nested inside wrapper divs)
      const textDiv = button.querySelector(".font-ringside-ssm-bold");

      if (isSelected) {
        button.classList.remove(
          "border",
          "border-grayscale-coal-300",
          "bg-white",
        );
        button.classList.add(
          "border-[3px]",
          "border-black-warm",
          "bg-[#BDB0934D]",
        );
        if (textDiv) {
          textDiv.classList.remove("text-grayscale-coal-500");
          textDiv.classList.add("text-grayscale-coal-900");
        }
      } else {
        button.classList.remove(
          "border-[3px]",
          "border-black-warm",
          "bg-[#BDB0934D]",
        );
        button.classList.add("border", "border-grayscale-coal-300", "bg-white");
        if (textDiv) {
          textDiv.classList.remove("text-grayscale-coal-900");
          textDiv.classList.add("text-grayscale-coal-500");
        }
      }
    }
  }

  /**
   * Handles click on a frequency option button
   * @param {MouseEvent} event
   */
  selectFrequency(event) {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const button =
      target instanceof HTMLButtonElement ? target : target?.closest("button");
    if (!button) return;

    const frequency = button.dataset.frequency || "";
    const sellingPlanId = button.dataset.sellingPlanId;

    this.#updateSelectedButton(frequency);

    this.dispatchEvent(
      new SellingPlanUpdateEvent(
        {
          sellingPlanId: sellingPlanId || null,
          type: "subscription",
          frequency,
        },
        this.id || "gr-frequency-modal",
      ),
    );

    // Close the modal after selection
    this.closeDialog();

    // Sync the frequency dropdown in gr-product-options
    this.#syncFrequencyDropdown(frequency);
  }

  /**
   * Syncs the frequency dropdown in gr-product-options with the modal selection
   * @param {string} frequency
   */
  #syncFrequencyDropdown(frequency) {
    // Check if there's an open added-to-cart drawer first, use its product options
    const openDrawer = document.querySelector(
      "added-to-cart-drawer dialog[open]",
    );
    let productOptions = null;

    if (openDrawer) {
      productOptions = openDrawer
        .closest("added-to-cart-drawer")
        ?.querySelector("gr-product-options");
    }

    // Fallback to finding it relative to this modal
    if (!productOptions) {
      productOptions =
        this.closest("gr-product-options") ||
        this.#eventTarget?.querySelector("gr-product-options");
    }

    if (!productOptions) return;

    const frequencySelect = productOptions.querySelector(
      '[ref="frequencySelect"]',
    );
    if (!(frequencySelect instanceof HTMLSelectElement)) return;

    for (const option of frequencySelect.options) {
      if (option.value === frequency) {
        frequencySelect.value = frequency;
        frequencySelect.dispatchEvent(new Event("change", { bubbles: true }));
        break;
      }
    }

    // Ensure subscription option is selected
    const subscriptionOption = productOptions.querySelector(
      '[ref="subscriptionOption"]',
    );
    if (
      subscriptionOption instanceof HTMLElement &&
      subscriptionOption.getAttribute("data-selected") !== "true"
    ) {
      subscriptionOption.click();
    }
  }
}

if (!customElements.get("gr-frequency-modal")) {
  customElements.define("gr-frequency-modal", GRFrequencyModal);
}

export { GRFrequencyModal };
